/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import {BaseChipSelectorZoneComponent} from './base-chip-selector-zone.component';

import {StatefulComponent} from '../stateful/stateful.component';
import {ChipSelectorStateModel} from './chip-selector-state.model';
import {Subscription} from 'rxjs';
import {BaseChipSelectorZoneInputModel} from './base-chip-selector-zone-input.model';
import {BaseChipSelectorZoneOutputModel} from './base-chip-selector-zone-output.model';

/**
 * A chip component that allows the user to change its value.
 */
@Component({
    selector: 'app-chip-selector',
    templateUrl: './chip-selector.component.html',
    styleUrls: ['./chip-selector.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChipSelectorComponent extends StatefulComponent<ChipSelectorStateModel> implements OnDestroy, AfterViewInit {
    valueMap = new Map<BaseChipSelectorZoneInputModel, any>();

    /**
     * Array of tuples of components and their options.
     */
    @Input()
    readonly zones: readonly BaseChipSelectorZoneInputModel[] = [];

    /**
     * The boolean value to enable or disable the delete icon
     */
    @Input()
    readonly isDeleteAvailable:  boolean = true;

    /**
     * Emits when the user finishes editing.
     */
    @Output()
    readonly close = new EventEmitter<BaseChipSelectorZoneOutputModel[]>();

    /**
     * Emits when the user clicks the X button.
     */
    @Output()
    readonly delete = new EventEmitter<BaseChipSelectorZoneOutputModel[]>();

    /**
     * Emits the zone with the value when the user changes something.
     */
    @Output()
    readonly change = new EventEmitter<BaseChipSelectorZoneOutputModel>();

    @ViewChild('zones', {read: ViewContainerRef})
    private zonesContainer: ViewContainerRef;

    @ViewChild('deleteButton', {read: ElementRef})
    private deleteElementRef: ElementRef;

    private components: ComponentRef<BaseChipSelectorZoneComponent>[] = [];
    private subscriptions = new Subscription();

    constructor(private elementRef: ElementRef,
                private changeDetectorRef: ChangeDetectorRef,
                @Inject(DOCUMENT) private document: Document) {
        super();

        // Adding "tabindex" attribute with a negative value so it handles focus events natively
        this.elementRef.nativeElement.setAttribute('tabindex', '-1');

        this.elementRef.nativeElement.addEventListener('focusin', this.gotFocus);
        this.elementRef.nativeElement.addEventListener('focusout', this.lostFocus);
    }

    get forceShowDeleteClass(): string {
        let cssClass = '';

        if (this.state.isActive) {
            cssClass = 'visible';
        }

        return cssClass;
    }

    get values(): BaseChipSelectorZoneOutputModel[] {
        return [...this.valueMap].map(([zone, value]) => ({
            value,
            type: zone.type,
            input: zone.input,
            isEdit: this.chipSelectorIsEdit
        }));
    }

    get chipSelectorIsEdit(): boolean {
        return this.components?.map((item) => item.instance?.state?.isEditMode)[0];
    }

    ngAfterViewInit(): void {
        let isActive = false;

        this.zones.forEach((zone) => {
            const component = this.zonesContainer.createComponent(zone.type);

            Object.assign(component.instance, zone.input);
            this.valueMap.set(zone, zone.input.initialValue);

            if (zone.input.initialValue == null) {
                isActive = true;
                component.instance.setEditMode(true);
            }

            this.subscriptions.add(component.instance.change.subscribe((value: unknown) => {
                this.change.emit({isEdit: false, ...zone, value});
                this.valueMap.set(zone, value);
            }));

            component.changeDetectorRef.detectChanges();

            this.components.push(component);
        });

        if (isActive) {
            this.activate();
            this.changeDetectorRef.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.components.forEach((component) => {
            component.destroy();
        });
        this.subscriptions.unsubscribe();
    }

    saveChanges(): void {
        this.storeState({isActive: false});

        this.components.forEach((component) => {
            component.instance.setViewMode();
        });
    }

    activate(hasFocus = true): void {
        this.storeState({isActive: true});

        this.components.forEach((component) => {
            component.instance.setEditMode();
        });

        if (hasFocus) {
            this.elementRef.nativeElement.focus();
        }
    }

    /**
     * <p>Event handler for when the component has gotten focus.
     * <p>Note: This is defined as a arrow function so we don't need to manually bind the function to <i><code>this<code></i>. If changed it
     * <b><i>WILL DEFINITELY</i></b> break the application. Do not change this to a normal function without rebinding it on the constructor.
     */
    gotFocus = (event: FocusEvent): void => {
        // If the FocusEvent originates from FocusEvent.currentTarget, the FocusEvent.relatedTarget will be null
        this.activate(event.relatedTarget != null);
    };

    /**
     * <p>Event handler for when the component has lost focus.
     * <p>Note: This is defined as a arrow function so we don't need to manually bind the function to <i><code>this<code></i>. If changed it
     * <b><i>WILL DEFINITELY</i></b> break the application. Do not change this to a normal function without rebinding it on the constructor.
     */
    lostFocus = (event: FocusEvent): void => {
        if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement)) {
            event.preventDefault();
        } else {
            this.saveChanges();
            this.close.emit(this.values);
        }
    };
}
